import styled, { css, keyframes } from 'styled-components';

import { Text } from '../../../../components';
import { FTUX_PICK } from '../../../../theme/ftux-tokens';
import { hoverWash } from '../../../../theme/mixins';
import {
  ANIM,
  COLORS,
  IMPORTANT_NOW,
  IMPORTANT_NOW_TONE,
  MOBILE,
  REDUCED_MOTION,
  SPACE,
  SPOTLIGHT_ANIM,
  SPOTLIGHT_SHELL,
  SPOTLIGHT_STRIP_MASK_BOTH,
  SPOTLIGHT_STRIP_MASK_LEFT,
  SPOTLIGHT_STRIP_MASK_RIGHT,
  type ImportantNowTone,
} from '../../../../theme/tokens';
import { AuroraField } from '../BrandDeploymentStrip/AuroraField';
import { GAUGE_COLUMNS, GAUGE_GAP_PX } from '../OptimizationScore/OptimizationScore.styles';

/* Port of ows-ftux's Home Spotlight (src/pages/home/components/Spotlight), itself a port of this studio's ImportantNow:
   the prototype's values, through tokens of the same names. */

/** The section's corners: the aurora and its glass clip to them (a little rounder than the cards' 16px). */
const ROOT_RADIUS = '24px';
/** The section's padding and border: the strip sits this far in from the page's edges (desktop). The pick card's
 *  width is computed from them (SpotlightPick.styles). */
export const ROOT_PAD_PX = 24;
export const ROOT_BORDER_PX = 1;
export const ROOT_INSET_PX = ROOT_PAD_PX + ROOT_BORDER_PX;

/** The section arriving with a fresh card: down into place. */
const rootIn = keyframes`
  from { opacity: 0; transform: translateY(-${SPOTLIGHT_ANIM.ROOT_DROP_PX}px); }
  to { opacity: 1; transform: none; }
`;

/** A fresh card: up into place, a beat after the section. */
const cardIn = keyframes`
  from { opacity: 0; transform: translateY(${SPOTLIGHT_ANIM.CARD_RISE_PX}px) scale(${SPOTLIGHT_ANIM.CARD_SCALE_FROM}); }
  to { opacity: 1; transform: none; }
`;

/**
 * The section's surface is Lumio's (an aurora in Lumio's colours instead of a static fill): a near-black base, the
 * drifting magenta-cyan aurora gathered at its foot, frosted glass over it, the header and the cards on top. Light:
 * a sunken light-grey base under a white frost, the aurora's pastel ink kept (SPOTLIGHT_SHELL's light pins).
 */
export const Root = styled.section<{ $enter: boolean }>`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  /* Rounded clipping for the composited aurora and glass (overflow alone leaks their square corners). */
  clip-path: inset(0 round ${ROOT_RADIUS});
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[28]};
  padding: ${SPACE[ROOT_PAD_PX]};
  /* Separate the banner from the content below it (Business Optimization Score). */
  margin-bottom: ${SPACE[20]};
  border: ${ROOT_BORDER_PX}px solid ${IMPORTANT_NOW.shellBorder};
  border-radius: ${ROOT_RADIUS};
  background: ${SPOTLIGHT_SHELL.surface};
  background-clip: padding-box;
  ${({ $enter }) =>
    $enter &&
    css`
      animation: ${rootIn} ${SPOTLIGHT_ANIM.ROOT_IN_MS}ms ${SPOTLIGHT_ANIM.EASE} backwards;
    `}

  ${REDUCED_MOTION} {
    animation: none;
  }

  ${MOBILE} {
    gap: ${SPACE[20]};
    padding: ${SPACE[16]};
  }
`;

/**
 * The drifting aurora behind the glass (the studio's WebGL AuroraField, magenta to cyan and back), dialled back to a
 * soft glow and flipped so it gathers at the section's foot, under the cards rather than behind the header. It rounds
 * itself: flipped, it is composited on its own layer, where the section's clip would square its corners. Reduced
 * motion: one still frame. Light: its ink-on-paper model (AuroraField follows the theme).
 */
export const RootAurora = styled(AuroraField)`
  z-index: -2;
  border-radius: inherit;
  opacity: 0.5;
  transform: scaleY(-1);
`;

/** Frosted glass in front of the aurora: the blur diffuses the moving colour into soft light, the tint keeps the
 *  section reading as a surface, a faint top line sells the glass edge. */
export const RootGlass = styled.span`
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  pointer-events: none;
  background: ${SPOTLIGHT_SHELL.glass};
  backdrop-filter: blur(40px) saturate(1.2);
  -webkit-backdrop-filter: blur(40px) saturate(1.2);
  border-top: 1px solid ${SPOTLIGHT_SHELL.glassEdge};
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE[12]};
`;

/** Title cluster: the bulb, the "Spotlight" eyebrow and the count badge. */
export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  min-width: 0;
`;

/** The header's lit bulb: its glow may spill past its box. Light: unlit (a light glow cannot read on the light
 *  shell), its ring in the header's ink. */
export const Bulb = styled.svg`
  display: block;
  flex: 0 0 auto;
  overflow: visible;

  :root[data-ows-theme='light'] & path {
    filter: none;
  }
`;

/**
 * The section's name (Figma Circular Std Light 20), a quiet label (the user, 2026-10-23: it competed with the card's
 * title): light, in the grey of the pick's "Getting started", so the card's title is the one strong white line.
 */
export const HeaderLabel = styled(Text)`
  font-size: 20px;
  font-weight: 300;
  line-height: 1.2;
  color: ${FTUX_PICK.eyebrow};
`;

/** Count of cards in the strip, shown beside the title once there is more than one: flat, as the arrows (a soft fill,
 *  no ring; the user, 2026-10-21). */
export const HeaderCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: ${SPACE[0]} ${SPACE[8]};
  border-radius: 100px;
  background: ${IMPORTANT_NOW.affordanceBg};
  color: ${IMPORTANT_NOW.cardTitle};
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
`;

/** Prev/next arrows, right-aligned in the header: both whenever the strip can scroll at all, neither when everything
 *  fits (useStripScroll), so the header's width never changes while scrolling. */
export const HeaderNav = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
`;

/**
 * A nav arrow, flat (a soft fill, no ring; the user, 2026-10-21). Hovered, the fill deepens and the arrow lights up.
 * Disabled at its end of the strip (paging stops there): faded, no hover.
 */
export const NavButton = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 100px;
  border: 0;
  background: ${IMPORTANT_NOW.affordanceBg};
  color: ${IMPORTANT_NOW.affordanceBorder};
  cursor: pointer;
  transition:
    color ${ANIM.INTERACT_MS}ms ease,
    background ${ANIM.INTERACT_MS}ms ease,
    opacity ${ANIM.INTERACT_MS}ms ease;

  &:hover:not(:disabled) {
    background: color-mix(in srgb, ${IMPORTANT_NOW.affordanceBorder} 22%, transparent);
    color: ${IMPORTANT_NOW.headerAccent};
  }

  &:disabled {
    color: ${COLORS.textMuted};
    opacity: 0.45;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${IMPORTANT_NOW.headerAccent};
    outline-offset: 2px;
  }
`;

/**
 * A card's width, the pick's and every notice's alike (the user, 2026-10-14 and 2026-10-19): a score card's under the
 * section (Product's: a third of the page, less the gaps between them), less the strip's inset from the page (the
 * section's border and padding) and the section's padding once more, so it ends that far short of where Product ends.
 * In the strip's own width (100%), which is the page's less that inset on both sides.
 */
const CARD_W = `calc((100% + ${2 * ROOT_INSET_PX}px - ${(GAUGE_COLUMNS - 1) * GAUGE_GAP_PX}px) / ${GAUGE_COLUMNS} - ${
  ROOT_INSET_PX + ROOT_PAD_PX
}px)`;
/** A card's least width: its title and its longest button with the quiet second on one row. */
const CARD_MIN_PX = 320;

/**
 * Every Spotlight card's width (CARD_W, never narrower than CARD_MIN_PX nor wider than the strip); on a phone 86% (the
 * next card peeks), the strip's whole width alone (nothing to peek at).
 */
export const cardWidth = css`
  && {
    width: min(100%, max(${CARD_MIN_PX}px, ${CARD_W}));
  }

  ${MOBILE} {
    && {
      width: 86%;
    }

    &&:only-child {
      width: 100%;
    }
  }
`;

/** The edge-fade mask, lit on the side(s) with hidden cards so a fully visible edge stays crisp. */
const stripMask = (fadeLeft: boolean, fadeRight: boolean): string => {
  if (fadeLeft && fadeRight) return SPOTLIGHT_STRIP_MASK_BOTH;
  if (fadeLeft) return SPOTLIGHT_STRIP_MASK_LEFT;
  if (fadeRight) return SPOTLIGHT_STRIP_MASK_RIGHT;
  return 'none';
};

/**
 * The strip: the cards side by side, snapping one card flush to its left edge, scrolling when they overflow (the
 * header's arrows drive it; the scrollbar is hidden). Each card sets its own width (cardWidth).
 */
export const Cards = styled.div<{ $fadeLeft: boolean; $fadeRight: boolean }>`
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: ${SPACE[16]};
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  -ms-overflow-style: none;
  -webkit-mask-image: ${({ $fadeLeft, $fadeRight }) => stripMask($fadeLeft, $fadeRight)};
  mask-image: ${({ $fadeLeft, $fadeRight }) => stripMask($fadeLeft, $fadeRight)};

  &::-webkit-scrollbar {
    display: none;
  }

  & > * {
    flex: 0 0 auto;
    scroll-snap-align: start;
  }

  & > [data-fresh='true'] {
    animation: ${cardIn} ${SPOTLIGHT_ANIM.CARD_IN_MS}ms ${SPOTLIGHT_ANIM.EASE} ${SPOTLIGHT_ANIM.CARD_IN_DELAY_MS}ms
      backwards;
  }

  /* Reduced motion: paging jumps instead of gliding, and the cards arrive still. */
  ${REDUCED_MOTION} {
    scroll-behavior: auto;

    & > [data-fresh='true'] {
      animation: none;
    }
  }

  ${MOBILE} {
    gap: ${SPACE[12]};
  }
`;

/** A notice card's corners, as the pick's (SpotlightPick.styles). */
const CARD_RADIUS = '20px';

/**
 * A notice card, in the pick's shape (the user, 2026-10-19; its parts are the pick's own, SpotlightPick.styles): the
 * pick's corners and padding, its parts on its gaps, over the tone's glow (the notice's own colour). A one-verb card is
 * the control itself (its CTA a span, so no button nests in a button); a two-verb card renders real buttons and is not
 * itself clickable.
 */
export const Card = styled.div<{ $tone: ImportantNowTone; $clickable: boolean }>`
  position: relative;
  isolation: isolate;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  padding: ${SPACE[16]};
  border: 1px solid ${IMPORTANT_NOW.cardBorder};
  border-radius: ${CARD_RADIUS};
  overflow: hidden;
  background: ${({ $tone }) => IMPORTANT_NOW_TONE[$tone].bg};
  background-clip: padding-box;
  ${cardWidth}

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
      ${hoverWash}

      &:focus-visible {
        outline: 2px solid ${IMPORTANT_NOW.headerAccent};
        outline-offset: 2px;
      }
    `}
`;

/** The buttons' row, the pick's buttons' height: a card with nothing to do keeps it empty, so every card keeps one
 *  height (a one-line eyebrow, the strip's title lines, a three-line line, this row). */
const ACTIONS_PX = 40;

/** A card with nothing to do: its buttons' row, empty. */
export const NoActions = styled.span`
  flex: 0 0 ${ACTIONS_PX}px;
`;

/** The card's parts over its backdrop: the heading, the line, the buttons pinned to the foot (the strip stretches
 *  every card to the tallest). */
export const CardContent = styled.div`
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};

  & > [data-card='actions'] {
    margin-top: auto;
  }
`;
